// i18n.js
'use strict';
import I18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import env from './env.js';
import getResourceUrl from './getResourceUrl.js';
import { resourceLog } from './debug.js';

const isOnClient = process && !process.release;

export const LANGUAGE_QUERY_NAME = 'lang';

export const localeConvertor = ({ locale, isISO639 = false }) => {
  let lng = typeof locale === 'string' ? locale : locale?.[0];
  switch (lng?.toLowerCase?.()) {
    case 'zh':
    case 'zh-cn':
    case 'zh-hans':
    case 'zh-hans-cn':
      return isISO639 ? 'zh' : 'zh-CN';
    case 'ja-jp':
      return 'ja';
    case 'en':
    case 'en-us':
      return 'en';
    default:
      if (lng?.startsWith?.('zh-')) {
        return isISO639 ? 'zh-hant' : 'zh-TW';
      }
      if (lng && /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{0,5}){0,2}-*$/.exec(lng)) {
        return lng.replace(/-*$/, '');
      }
      return 'en';
  }
};

const i18nOption = {
  fallbackLng: code => {
    const language = code?.toLowerCase?.();
    if (language?.startsWith?.('zh-')) {
      return ['zh-TW', 'en'];
    } else {
      return ['en'];
    }
  },
  interpolation: { prefix: '{', suffix: '}', skipOnVariables: false },
  debug: env.DEBUG_I18N === 'true',
  // XXX: fix the bug when change language on the setting page
  react: {
    useSuspense: false,
  },
};

// eslint-disable-next-line no-unused-vars
// ref: https://github.com/i18next/i18next-browser-languageDetector
const languageDetectorOption = {
  detection: {
    order: [
      'querystring',
      'cookie',
      'navigator', // Google crawler and SSR don't have navigator
      // 'htmlTag', // Client side default (sync with SSR)
      // 'header', // SSR Accept-Language header (CDN)
      // 'localStorage',
      // 'path',
      // 'subdomain',
    ],
    lookupQuerystring: 'lang',
    caches: isOnClient ? ['cookie'] : [],
    // default is Session Cookie, see: https://stackoverflow.com/a/36421888
    cookieMinutes: 7 * 24 * 60, // 7 days

    // fallback to a similar whitelist language
    // Example 1: Browser language is 'es'
    // if 'es' is not found in whitelist, first fallback to any whitelist language that starts with 'es-', then fallback to fallbackLng ('es' -> 'es-*' -> fallbackLng)
    // Example 2: Browser language is 'es-MX'
    // if 'es-MX' is not found in whitelist, first fallback to 'es', then fallback to 'es-*', then fallback to fallbackLng ('es-MX' -> 'es' -> 'es-*' -> fallbackLng)
    checkForSimilarInWhitelist: true,
  },
};

let _saveToFileSystem;

export const setI18nFileSystemFallback = ({ save }) => {
  if (!isOnClient) {
    _saveToFileSystem = save;
  }
};

const saveToFileSystem = async ({ lang, langPack }) => {
  if (!isOnClient && _saveToFileSystem) {
    try {
      await _saveToFileSystem({ lang, langPack });
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }
};

export const httpApiOption = {
  backend: {
    reloadInterval: isOnClient ? false : 5 * 60 * 1000, // 5 minutes
    loadPath: locale => localeConvertor({ locale }), // passing i18n locale.
    parse: data => data, // to ignore `i18next-http-backend` JSON.parse
    request: async (options, locale, payload, callback) => {
      let status,
        langPack = {},
        enableFallback = env.ENABLE_I18N_LOCAL_FALLBACK === '1',
        shouldUseFallback = false;

      const url = getResourceUrl({ endpoint: '/translations' });
      const lang = localeConvertor({ locale, isISO639: true });
      url.searchParams.append('lang', lang);

      try {
        const res = await fetch(url);
        status = res.status;
        langPack = await res.json();

        if (enableFallback) saveToFileSystem({ lang, langPack });
      } catch (err) {
        resourceLog('i18n fetch /translations error', err);
        if (enableFallback) shouldUseFallback = true;
        else return callback(err);
      }

      if (enableFallback && shouldUseFallback) {
        try {
          const fallbackRes = await fetch(
            `/locale/${localeConvertor({ locale })}.json`
          );
          status = fallbackRes.status;
          langPack = await fallbackRes.json();
        } catch (err) {
          resourceLog('i18n fetch /locale error', err);
          return callback(err);
        }
      }

      const serverSideLangPack = isOnClient
        ? window.initialI18nStore?.[locale]?.translation
        : false;
      if (serverSideLangPack) {
        status = 200;
        Object.keys(serverSideLangPack).forEach(key => {
          if (!langPack[key] && serverSideLangPack[key]) {
            langPack[key] = serverSideLangPack[key];
          }
        });
      }

      resourceLog('i18n loaded langpack', { lang, status });
      callback(null, { data: langPack, status });
    },
  },
};

const option = {
  ...i18nOption,
  ...httpApiOption,
  ...languageDetectorOption,
};

export const i18n = isOnClient
  ? I18n.createInstance(option).use(HttpApi).use(LanguageDetector)
  : I18n.createInstance(option).use(HttpApi);

export default i18n;
